import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import { Pagination, Tooltip } from "@mui/material";

import MDBox from "components/MDBox";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import {
  CUSTOM_STRATEGY_STATUS_COLOR,
  CUSTOM_STRATEGY_STATUS_LABEL,
  CustomStrategyStatus,
} from "constants/strategy";
import { StrategyItem, StrategyService, StrategyStats } from "service/strategy/service";

const STATUS_TABS: { key: CustomStrategyStatus | "ALL"; label: string; color: string }[] = [
  { key: "ALL", label: "총 신청", color: "#6b7280" },
  { key: CustomStrategyStatus.NEW, label: "신규", color: "#3b82f6" },
  { key: CustomStrategyStatus.CONTACTING, label: "연락중", color: "#f97316" },
  { key: CustomStrategyStatus.RECOMMENDED, label: "물건추천", color: "#a855f7" },
  { key: CustomStrategyStatus.CONTRACT, label: "계약", color: "#22c55e" },
];

const STATUS_FILTER_OPTIONS: { value: "ALL" | CustomStrategyStatus; label: string }[] = [
  { value: "ALL", label: "전체" },
  ...Object.values(CustomStrategyStatus).map((s) => ({
    value: s,
    label: CUSTOM_STRATEGY_STATUS_LABEL[s],
  })),
];

const SORT_OPTIONS = [
  { value: "CD", label: "최신순" },
  { value: "CA", label: "오래된순" },
];

const ACTION_DEFS: { key: keyof StrategyItem; icon: string; label: string }[] = [
  { key: "recommend_done", icon: "description", label: "추천완료" },
  { key: "email_sent", icon: "mail_outline", label: "이메일" },
  { key: "kakao_sent", icon: "chat_bubble_outline", label: "카카오" },
  { key: "sms_sent", icon: "smartphone", label: "문자" },
  { key: "call_done", icon: "phone", label: "전화" },
];

const STATS_KEY_BY_STATUS: Record<CustomStrategyStatus, keyof StrategyStats> = {
  [CustomStrategyStatus.NEW]: "new_count",
  [CustomStrategyStatus.CONTACTING]: "contacting_count",
  [CustomStrategyStatus.CONSULTED]: "consulted_count",
  [CustomStrategyStatus.RECOMMENDED]: "recommended_count",
  [CustomStrategyStatus.CONTRACT]: "contract_count",
  [CustomStrategyStatus.CLOSED]: "closed_count",
};

const EMPTY_STATS: StrategyStats = {
  new_count: "0",
  contacting_count: "0",
  consulted_count: "0",
  recommended_count: "0",
  contract_count: "0",
  closed_count: "0",
};

const formatKST = (utc: string | null | undefined): string => {
  if (!utc) return "";
  const iso = utc.includes("T")
    ? utc.endsWith("Z")
      ? utc
      : `${utc}Z`
    : `${utc.replace(" ", "T")}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return utc;
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${kst.getUTCFullYear()}-${pad(kst.getUTCMonth() + 1)}-${pad(kst.getUTCDate())} ${pad(
    kst.getUTCHours()
  )}:${pad(kst.getUTCMinutes())}`;
};

const limit = 10;

function StrategyList(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<"ALL" | CustomStrategyStatus>("ALL");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("CD");
  const [currentPage, setCurrentPage] = useState(1);

  const [list, setList] = useState<StrategyItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<StrategyStats>(EMPTY_STATS);

  const navigate = useNavigate();

  const fetchList = async () => {
    try {
      const res = await StrategyService.list({
        search,
        status: statusFilter === "ALL" ? "" : statusFilter,
        sort,
        limit,
        page: currentPage,
      });
      setList(res?.list ?? []);
      setTotalCount(res?.totalCount ?? 0);
      if (res?.stats) setStats(res.stats);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchList();
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    fetchList();
  }, [statusFilter, sort, search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  const totalForAll = useMemo(() => {
    return Object.values(stats).reduce((sum, v) => sum + Number(v || 0), 0);
  }, [stats]);

  const getCount = (key: CustomStrategyStatus | "ALL"): number => {
    if (key === "ALL") return totalForAll;
    return Number(stats[STATS_KEY_BY_STATUS[key]] || 0);
  };

  const handleSearch = () => {
    setSearch(searchInput);
  };

  const handleCsv = async () => {
    try {
      const query = new URLSearchParams();
      if (search) query.set("search", search);
      if (statusFilter !== "ALL") query.set("status", statusFilter);
      if (sort) query.set("sort", sort);

      const token = localStorage.getItem("accessToken");
      const res = await fetch(
        `${process.env.REACT_APP_HOST}admin/custom-strategy/csv?${query.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`CSV 다운로드 실패: ${res.status}`);

      const disposition = res.headers.get("content-disposition") ?? "";
      const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
      const filename = match
        ? decodeURIComponent(match[1])
        : `strategy_${new Date().toISOString().slice(0, 10)}.csv`;

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
      alert("CSV 다운로드에 실패했습니다.");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
            gap: 12,
            marginBottom: 16,
          }}
        >
          {STATUS_TABS.map((s) => {
            const count = getCount(s.key);
            const active = statusFilter === s.key;
            return (
              <Card
                key={s.key}
                sx={{
                  p: "16px 20px",
                  borderLeft: `4px solid ${s.color}`,
                  cursor: "pointer",
                  transition: "transform 0.15s ease",
                  outline: active ? `2px solid ${s.color}` : "none",
                  "&:hover": { transform: "translateY(-2px)" },
                }}
                onClick={() => setStatusFilter(s.key)}
              >
                <div
                  style={{
                    fontSize: 13,
                    color: "#6b7280",
                    fontFamily: "Pretendard",
                    fontWeight: 500,
                    marginBottom: 6,
                  }}
                >
                  {s.label}
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                  <span
                    style={{
                      fontSize: 26,
                      fontWeight: 700,
                      color: s.color,
                      fontFamily: "Pretendard",
                    }}
                  >
                    {count}
                  </span>
                  <span style={{ fontSize: 14, color: "#374151", fontFamily: "Pretendard" }}>
                    건
                  </span>
                </div>
              </Card>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 16,
            padding: "0 4px",
          }}
        >
          <div style={{ display: "flex", flex: 1, minWidth: 320, gap: 6, alignItems: "center" }}>
            <input
              type="text"
              placeholder="고객명, 연락처"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              style={{
                flex: 1,
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                fontSize: 14,
                fontFamily: "Pretendard",
              }}
            />
            <div
              role="button"
              onClick={handleSearch}
              style={{
                padding: "10px 18px",
                backgroundColor: "rgb(30, 155, 107)",
                color: "white",
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 6,
                cursor: "pointer",
                fontFamily: "Pretendard",
              }}
            >
              검색
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "ALL" | CustomStrategyStatus)}
              style={{
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                fontSize: 14,
                fontFamily: "Pretendard",
                backgroundColor: "white",
              }}
            >
              {STATUS_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              style={{
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                fontSize: 14,
                fontFamily: "Pretendard",
                backgroundColor: "white",
              }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <div
              role="button"
              onClick={handleCsv}
              style={{
                padding: "10px 14px",
                borderRadius: 6,
                border: "1px solid #111827",
                backgroundColor: "white",
                display: "flex",
                gap: 6,
                alignItems: "center",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "Pretendard",
              }}
            >
              <Icon fontSize="small">file_download</Icon>
              CSV 다운로드
            </div>
          </div>
        </div>

        <Card>
          <div style={{ padding: 16 }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ minWidth: 1000, width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#374151",
                      textAlign: "center",
                      fontFamily: "Pretendard",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <th style={{ padding: "14px 8px" }}>No</th>
                    <th style={{ padding: "14px 8px" }}>고객명</th>
                    <th style={{ padding: "14px 8px" }}>연락처</th>
                    <th style={{ padding: "14px 8px" }}>이메일</th>
                    <th style={{ padding: "14px 8px" }}>희망서비스</th>
                    <th style={{ padding: "14px 8px" }}>투자범위</th>
                    <th style={{ padding: "14px 8px" }}>신청일</th>
                    <th style={{ padding: "14px 8px" }}>액션체크</th>
                    <th style={{ padding: "14px 8px" }}>상태</th>
                    <th style={{ padding: "14px 8px" }}>상세보기</th>
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        style={{
                          padding: "60px 0",
                          textAlign: "center",
                          color: "#9ca3af",
                          fontFamily: "Pretendard",
                          fontSize: 14,
                        }}
                      >
                        조회된 신청이 없습니다.
                      </td>
                    </tr>
                  )}
                  {list.map((row) => {
                    const color = CUSTOM_STRATEGY_STATUS_COLOR[row.status] ?? "#6b7280";
                    const label = CUSTOM_STRATEGY_STATUS_LABEL[row.status] ?? row.status;
                    return (
                      <tr
                        key={row.strategy_id}
                        style={{
                          fontSize: 13,
                          color: "#000000DE",
                          textAlign: "center",
                          fontFamily: "Pretendard",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        <td style={{ padding: "16px 8px" }}>{row.strategy_id}</td>
                        <td style={{ padding: "16px 8px", fontWeight: 600 }}>{row.user_name}</td>
                        <td style={{ padding: "16px 8px" }}>{row.user_tel?.trim() || "-"}</td>
                        <td style={{ padding: "16px 8px" }}>{row.user_email ?? "-"}</td>
                        <td style={{ padding: "16px 8px" }}>{row.service_type ?? "-"}</td>
                        <td style={{ padding: "16px 8px", whiteSpace: "nowrap" }}>
                          {row.budget_range ?? "-"}
                        </td>
                        <td style={{ padding: "16px 8px", whiteSpace: "nowrap" }}>
                          {formatKST(row.create_time)}
                        </td>
                        <td style={{ padding: "16px 8px" }}>
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "4px 8px",
                              borderRadius: 999,
                              border: "1px solid #e5e7eb",
                            }}
                          >
                            {ACTION_DEFS.map((a) => {
                              const checked = row[a.key] === "Y";
                              return (
                                <Tooltip key={a.key} title={a.label}>
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      width: 20,
                                      height: 20,
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: checked ? "#1e9b6b" : "#d1d5db",
                                    }}
                                  >
                                    <Icon style={{ fontSize: 16 }}>{a.icon}</Icon>
                                  </span>
                                </Tooltip>
                              );
                            })}
                          </div>
                        </td>
                        <td style={{ padding: "16px 8px" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 10px",
                              borderRadius: 6,
                              backgroundColor: `${color}1A`,
                              color,
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            {label}
                          </span>
                        </td>
                        <td style={{ padding: "16px 8px" }}>
                          <button
                            type="button"
                            onClick={() => navigate(`/management/strategy/${row.strategy_id}`)}
                            style={{
                              padding: "6px 14px",
                              borderRadius: 6,
                              border: "none",
                              backgroundColor: "rgb(30, 155, 107)",
                              color: "white",
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: "pointer",
                              fontFamily: "Pretendard",
                            }}
                          >
                            상세보기
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, value) => setCurrentPage(value)}
                color="secondary"
                sx={{
                  "& .MuiPaginationItem-root": {
                    "&.Mui-selected": {
                      backgroundColor: "#1e9b6b",
                      color: "white",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "#1e9b6b",
                    },
                  },
                }}
              />
            </div>
          </div>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default StrategyList;
